import {
  CopyMode,
  ICopyDataFilter,
  ICopyOptions,
  ICopyPaddockDataRequest,
  ICopyTarget,
} from 'interfaces/ICopyPaddockData';
import { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { getInputPaddockIds } from 'utils/FarmUtils';

import {
  CopyTreeIndex,
  getCopyInputKey,
  getCopyPaddockKey,
  getInputKeysById,
} from './buildCopyTreeData';
import { applyCopyDataFilter, getCopySourceTasks } from './copyDataFilter';

/** The shape antd's Tree takes for `checkedKeys` under `checkStrictly`. */
export interface CopyTreeCheckedKeys {
  checked: string[];
  halfChecked: string[];
}

/** One panel's settings and selection; the swap exchanges them whole. */
export interface CopySideState {
  /** Filters the panel's list; on Destination it's also the year of any new document. */
  year: number;
  mode: InputTaskCategory;
  /** Ticked keys, in the order they were ticked. */
  keys: string[];
  /** Null until the user expands, collapses or ticks, so the defaults apply. */
  expandedKeys: string[] | null;
}

const getInputKeys = (keys: string[], index: CopyTreeIndex): string[] =>
  keys.filter((key) => index.get(key)?.kind === 'input');

const getEmptyPaddockKeys = (keys: string[], index: CopyTreeIndex) =>
  keys.filter((key) => {
    const info = index.get(key);
    return info?.kind === 'paddock' && info.isEmpty;
  });

/** Paddock keys whose tickable plans are all in `inputKeys`; the untickable Source doesn't count, as in antd. */
const getFullPaddockKeys = (inputKeys: string[], index: CopyTreeIndex) => {
  const checked = new Set(inputKeys);
  const seen = new Set<string>();
  const paddockKeys: string[] = [];

  inputKeys.forEach((key) => {
    const info = index.get(key);
    if (info?.kind !== 'input') return;
    const paddockKey = getCopyPaddockKey(info.paddockId);
    if (seen.has(paddockKey)) return;
    seen.add(paddockKey);
    const paddock = index.get(paddockKey);
    if (
      paddock?.kind === 'paddock' &&
      paddock.inputKeys.every((inputKey) => checked.has(inputKey))
    ) {
      paddockKeys.push(paddockKey);
    }
  });

  return paddockKeys;
};

/** Adds the same plans' keys under their other paddocks, so a plan linked to two ticks in both. */
export const withLinkedCopies = (inputKeys: string[], index: CopyTreeIndex) => {
  const byId = getInputKeysById(index);
  const result = new Set(inputKeys);
  inputKeys.forEach((key) => {
    const info = index.get(key);
    if (info?.kind !== 'input') return;
    byId.get(info.input.id)?.forEach((linkedKey) => result.add(linkedKey));
  });
  return Array.from(result);
};

/**
 * Drops keys the tree doesn't have and fits the rest to the panel. Single
 * keeps only the first ticked plan; multiple ticks a plan under every paddock
 * it links to and adds every fully ticked paddock;
 * paddocks-only keeps paddock keys alone.
 */
export const normaliseCopyKeys = (
  keys: string[],
  index: CopyTreeIndex,
  allowMultiple: boolean,
  paddocksOnly = false
): string[] => {
  if (paddocksOnly) {
    return keys.filter((key) => index.get(key)?.kind === 'paddock');
  }
  if (!allowMultiple) return getInputKeys(keys, index).slice(0, 1);
  const inputKeys = withLinkedCopies(getInputKeys(keys, index), index);

  return [
    ...inputKeys,
    ...getEmptyPaddockKeys(keys, index),
    ...getFullPaddockKeys(inputKeys, index),
  ];
};

/**
 * A tick on a single-document panel: ticking a plan replaces the selection,
 * and paddocks and farms are ignored. Many-document panels tick through
 * applyShownCheck (copyTreeChecks.ts), which reaches only shown rows.
 */
export const applySingleCopyCheck = (
  keys: string[],
  key: string,
  checked: boolean,
  index: CopyTreeIndex
): string[] => {
  if (index.get(key)?.kind !== 'input') return keys;
  return checked ? [key] : [];
};

/** Splits ticked keys into antd's checked / half-checked pair for display. */
export const toTreeCheckedKeys = (
  keys: string[],
  index: CopyTreeIndex
): CopyTreeCheckedKeys => {
  const checked = new Set(keys);
  const halfChecked = new Set<string>();

  getInputKeys(keys, index).forEach((key) => {
    const info = index.get(key);
    if (info?.kind !== 'input') return;
    const paddockKey = getCopyPaddockKey(info.paddockId);
    if (!checked.has(paddockKey)) halfChecked.add(paddockKey);
  });

  return { checked: keys, halfChecked: Array.from(halfChecked) };
};

/** The open plan's key in the source tree, so it opens pre-ticked. */
export const getOpenInputKeys = (
  openInput: IInput | undefined,
  sourceIndex: CopyTreeIndex
): string[] => {
  if (!openInput) return [];
  const inputKey = getInputPaddockIds(openInput)
    .map((paddockId) => getCopyInputKey(paddockId, openInput.id))
    .find((key) => sourceIndex.has(key));
  return inputKey ? [inputKey] : [];
};

/**
 * Carries the Destination ticks across a switch to or from Add as new: ticked
 * documents become their paddocks, and ticked paddocks become all their documents.
 */
export const convertDestinationKeys = (
  keys: string[],
  index: CopyTreeIndex,
  toPaddocksOnly: boolean
): string[] => {
  if (toPaddocksOnly) {
    const paddockKeys = keys.flatMap((key) => {
      const info = index.get(key);
      if (info?.kind === 'input') return [getCopyPaddockKey(info.paddockId)];
      return info?.kind === 'paddock' ? [key] : [];
    });
    return Array.from(new Set(paddockKeys));
  }

  const expanded = keys.flatMap((key) => {
    const info = index.get(key);
    return info?.kind === 'paddock' && !info.isEmpty ? info.inputKeys : [key];
  });
  return normaliseCopyKeys(Array.from(new Set(expanded)), index, true);
};

/** Destination's ticks saved on entering Add as new, with their paddocks at that moment. */
export interface SavedDocumentKeys {
  keys: string[];
  paddockKeys: string[];
}

/**
 * Leaving Add as new: brings back the documents ticked before it, for the
 * paddocks still ticked. Only paddocks first ticked in Add as new get all their
 * documents; a saved paddock whose documents have since gone (e.g. now the
 * Source) gets none.
 */
export const restoreDocumentKeys = (
  paddockKeys: string[],
  saved: SavedDocumentKeys,
  index: CopyTreeIndex
): string[] => {
  const ticked = new Set(paddockKeys);
  const savedPaddocks = new Set(saved.paddockKeys);
  const kept = saved.keys.filter((key) => {
    const info = index.get(key);
    return info?.kind === 'input'
      ? ticked.has(getCopyPaddockKey(info.paddockId))
      : ticked.has(key);
  });
  const added = paddockKeys.filter((key) => !savedPaddocks.has(key));
  return convertDestinationKeys([...kept, ...added], index, false);
};

export interface SwapCopySidesParams {
  source: CopySideState;
  destination: CopySideState;
  sourceIndex: CopyTreeIndex;
  destinationIndex: CopyTreeIndex;
  sourceAllowMultiple: boolean;
  destinationAllowMultiple: boolean;
}

/**
 * Exchanges the panels' year, mode, ticks and expanded nodes. Each side's
 * keys are refitted to the panel they move to, so a single-plan Source keeps
 * the first ticked document. Not offered in Add as new, where Destination
 * holds paddocks rather than documents.
 */
export const swapCopySides = ({
  source,
  destination,
  sourceIndex,
  destinationIndex,
  sourceAllowMultiple,
  destinationAllowMultiple,
}: SwapCopySidesParams): {
  source: CopySideState;
  destination: CopySideState;
} => ({
  source: {
    ...destination,
    keys: normaliseCopyKeys(
      destination.keys,
      destinationIndex,
      sourceAllowMultiple
    ),
  },
  destination: {
    ...source,
    keys: normaliseCopyKeys(source.keys, sourceIndex, destinationAllowMultiple),
  },
});

/** The ticked documents and empty paddocks the copy lands in; Add as new: the ticked paddocks. */
const getCopyTargets = (
  copyMode: CopyMode,
  keys: string[],
  index: CopyTreeIndex
): ICopyTarget[] => {
  const targets: ICopyTarget[] = [];
  // One per document, and one new document per paddock.
  const seen = new Set<string>();
  const addTarget = (target: ICopyTarget) => {
    const id =
      target.inputId === null
        ? `new:${target.paddockId}`
        : `input:${target.inputId}`;
    if (seen.has(id)) return;
    seen.add(id);
    targets.push(target);
  };

  keys.forEach((key) => {
    const info = index.get(key);
    if (info?.kind === 'input') {
      addTarget({
        farmId: info.farmId,
        paddockId: info.paddockId,
        inputId: copyMode === CopyMode.AddAsNew ? null : info.input.id,
      });
    } else if (
      info?.kind === 'paddock' &&
      (copyMode === CopyMode.AddAsNew || info.isEmpty)
    ) {
      addTarget({
        farmId: info.farmId,
        paddockId: info.paddockId,
        inputId: null,
      });
    }
  });

  return targets;
};

/** The ticked source plan, if any. */
export const getCopySourceInput = (
  keys: string[],
  index: CopyTreeIndex
): IInput | null => {
  const info = index.get(getInputKeys(keys, index)[0] ?? '');
  return info?.kind === 'input' ? info.input : null;
};

export interface BuildCopyRequestParams {
  orgId: string;
  copyMode: CopyMode;
  source: CopySideState;
  destination: CopySideState;
  sourceIndex: CopyTreeIndex;
  destinationIndex: CopyTreeIndex;
  /** The data filter for the ticked source; null while there's none. */
  filter: ICopyDataFilter | null;
  options: ICopyOptions;
}

/** The copy request for the current selection, or null until both sides have one. */
export const buildCopyRequest = ({
  orgId,
  copyMode,
  source,
  destination,
  sourceIndex,
  destinationIndex,
  filter,
  options,
}: BuildCopyRequestParams): ICopyPaddockDataRequest | null => {
  const sourceInput = getCopySourceInput(source.keys, sourceIndex);
  const targets = getCopyTargets(copyMode, destination.keys, destinationIndex);
  if (!orgId || !sourceInput || !filter || targets.length === 0) return null;

  return {
    orgId,
    copyMode,
    source: { inputId: sourceInput.id, mode: source.mode, filter },
    destination: { year: destination.year, mode: destination.mode, targets },
    options,
  };
};

export interface CopySummary {
  taskCount: number;
  stepCount: number;
  documentCount: number;
  /** Total area of the destinations with a known area, in hectares. */
  areaHa: number;
  /** Destinations left out of `areaHa` because their area isn't known. */
  unknownAreaCount: number;
}

/**
 * What the copy moves: the source's tasks and steps its filter keeps, and the
 * destinations' total area in ha. A ticked document counts its zone area, or
 * the paddocks it links to when that's missing or 0; a new document counts its
 * paddock. A destination with no known area is counted in `unknownAreaCount`.
 */
export const buildCopySummary = (
  request: ICopyPaddockDataRequest,
  sourceInput: IInput,
  destinationIndex: CopyTreeIndex
): CopySummary => {
  const copiedTasks = applyCopyDataFilter(
    getCopySourceTasks(sourceInput, request.source.mode),
    request.source.filter
  );

  const getIndexedPaddockAreaHa = (paddockId: string) => {
    const paddock = destinationIndex.get(getCopyPaddockKey(paddockId));
    return paddock?.kind === 'paddock' ? paddock.areaHa : null;
  };

  const getTargetAreaHa = (target: ICopyTarget): number | null => {
    if (target.inputId === null) {
      return getIndexedPaddockAreaHa(target.paddockId);
    }
    const keys = getInputKeysById(destinationIndex).get(target.inputId) ?? [];
    const document = destinationIndex.get(keys[0] ?? '');
    if (document?.kind !== 'input') return null;
    if (document.zoneAreaHa !== null) return document.zoneAreaHa;
    // A linked paddock missing from the tree (e.g. a hidden farm) would undercount.
    if (keys.length < getInputPaddockIds(document.input).length) return null;
    const paddockAreas = keys.map((key) => {
      const info = destinationIndex.get(key);
      return info?.kind === 'input'
        ? getIndexedPaddockAreaHa(info.paddockId)
        : null;
    });
    if (paddockAreas.length === 0 || paddockAreas.some((a) => a === null)) {
      return null;
    }
    return paddockAreas.reduce<number>((sum, a) => sum + (a ?? 0), 0);
  };

  let areaHa = 0;
  let unknownAreaCount = 0;
  request.destination.targets.forEach((target) => {
    const targetArea = getTargetAreaHa(target);
    if (targetArea === null) unknownAreaCount += 1;
    else areaHa += targetArea;
  });

  return {
    taskCount: copiedTasks.length,
    stepCount: copiedTasks.reduce(
      (total, { steps }) => total + steps.length,
      0
    ),
    documentCount: request.destination.targets.length,
    areaHa,
    unknownAreaCount,
  };
};
